import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { WhatsAppIcon } from "@/components/Icons";
import { BRAND } from "@/data/content";

const EASE = [0.16, 1, 0.3, 1];

const Line = ({ children, delay }) => (
  <span className="line-mask">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} id="top" className="relative min-h-[90vh] flex flex-col justify-center pt-28 pb-10 overflow-hidden" data-testid="hero">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 w-full grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-forest/20 bg-forest/5 px-4 py-2 mb-8">
            <ShieldCheck size={15} className="text-forest" />
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-forest">Independent Claim Advisory</span>
          </motion.div>

          <h1 className="text-5xl sm:text-6xl md:text-7xl font-extrabold tracking-tighter leading-[1.02] text-ink">
            <Line delay={0.15}>Reclaim what's</Line>
            <Line delay={0.28}>yours — without</Line>
            <Line delay={0.41}><span className="text-forest">the stress.</span></Line>
          </h1>

          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.7 }}
            className="mt-8 text-lg sm:text-xl text-zinc-600 max-w-xl leading-relaxed">
            We are the independent experts who fight insurers on your behalf — evaluating, documenting and negotiating your claim to a full, rightful settlement. Free initial claim review.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.7 }}
            className="mt-10 flex flex-col sm:flex-row gap-4">
            <a href="#contact" className="btn-primary group" data-testid="hero-cta-primary">
              Get a Free Claim Review
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <a href={BRAND.whatsapp} target="_blank" rel="noreferrer" className="btn-secondary" data-testid="hero-cta-whatsapp">
              <WhatsAppIcon size={20} /> Chat on WhatsApp
            </a>
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.4, duration: 1, ease: EASE }}
            className="relative rounded-3xl overflow-hidden aspect-[4/5] shadow-2xl grain">
            <motion.img
              src="https://static.prod-images.emergentagent.com/jobs/09933f45-7de3-4cc6-b08c-72e7b744e64a/images/a432383434e029abd0e45539e9a8210b89c96ce89a14a971267717170e1d63c9.jpeg"
              alt="Insurance claim advisor reviewing documents with clients"
              style={{ y: imgY, scale: imgScale }}
              loading="eager"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-ink/10 to-transparent" />
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.7 }}
            className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl border border-zinc-100 p-5 w-52">
            <div className="text-3xl font-extrabold text-forest tracking-tight">₹50 Cr+</div>
            <div className="text-xs text-zinc-500 mt-1">Claims assisted across India</div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
